// client/src/pages/conference/ConferencesList.js
//
// The way into a live class. The whole conferencing backend — 15 endpoints,
// two tables, a LiveKit SFU — shipped with no UI that could list a session or
// create one, so `/conference/:id` was reachable only by typing an ID you had
// no way to obtain. This is that missing surface.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import conferenceService, { canHostConferences } from '../../services/conferences';
import api, { describeApiError, fieldErrorsFrom } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  PageHeader,
  PageShell,
  Select,
  SkeletonCard,
  Tabs,
  Textarea,
  cx
} from '../../components/ui';
import AdminModal from '../../components/admin/AdminModal';
import {
  VideoCameraIcon,
  PlusIcon,
  UserCircleIcon,
  CalendarIcon,
  SignalIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const TABS = [
  { value: 'upcoming', label: 'Upcoming & live' },
  { value: 'ended', label: 'Past' },
  { value: 'all', label: 'All' }
];

const STATUS_TONES = { live: 'danger', scheduled: 'info', ended: 'neutral', cancelled: 'neutral' };
const STATUS_LABELS = { live: 'Live now', scheduled: 'Scheduled', ended: 'Ended', cancelled: 'Cancelled' };

/** Formats a scheduled time, or says it is unscheduled. */
const formatWhen = (iso) => {
  if (!iso) return 'No scheduled time';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'No scheduled time';
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const ConferenceCard = ({ conference, onJoin, canEnd, onEnd }) => {
  const isLive = conference.status === 'live';
  const hostName = conference.host
    ? `${conference.host.firstName} ${conference.host.lastName}`
    : 'Unknown host';

  return (
    <Card className="flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="break-words font-display text-base font-bold text-ink-950">{conference.title}</h3>
          <p className="mt-1 flex items-center gap-1.5 text-xs text-ink-600">
            <UserCircleIcon aria-hidden="true" className="h-3.5 w-3.5" />
            {hostName}
            {conference.organization ? ` · ${conference.organization.name}` : ''}
          </p>
        </div>
        <Badge tone={STATUS_TONES[conference.status] || 'neutral'} dot>
          {STATUS_LABELS[conference.status] || conference.status}
        </Badge>
      </div>

      {conference.description && (
        <p className="mt-3 line-clamp-2 text-sm text-ink-600">{conference.description}</p>
      )}

      <p className="mt-3 flex items-center gap-1.5 text-xs text-ink-500">
        <CalendarIcon aria-hidden="true" className="h-3.5 w-3.5" />
        {formatWhen(conference.scheduledStart)}
      </p>

      <div className="mt-5 flex items-center gap-2 pt-1">
        <Button
          size="sm"
          variant={isLive ? 'saffron' : 'secondary'}
          icon={isLive ? SignalIcon : VideoCameraIcon}
          onClick={() => onJoin(conference)}
          disabled={conference.status === 'ended'}
        >
          {isLive ? 'Join now' : conference.status === 'ended' ? 'Ended' : 'Open room'}
        </Button>
        {/* The room's own "End" control is unreachable whenever the room
            cannot open (live video not switched on, or a network failure),
            which left a scheduled class on everyone's list for good. */}
        {canEnd && (conference.status === 'scheduled' || conference.status === 'live') && (
          <Button size="sm" variant="ghost" onClick={() => onEnd(conference)}>
            {isLive ? 'End class' : 'Cancel class'}
          </Button>
        )}
      </div>
    </Card>
  );
};

const ConferencesList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canHost = canHostConferences(user?.role);
  // Without an institution, "my organization" would admit nobody (a platform
  // admin has none), so that choice is only offered to people who have one.
  const hasOrg = Boolean(user?.organizationId || user?.organization?.id);
  const defaultAccess = hasOrg ? 'organization' : 'public';

  const [conferences, setConferences] = useState([]);
  // `null` until the server says; older servers omit it, so only an explicit
  // `false` shows the warning.
  const [liveAvailable, setLiveAvailable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('upcoming');
  // `?new=1` is how a dashboard's "Create a live class" lands here with the
  // form already open, instead of on a list with one more button to find.
  const [searchParams, setSearchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(() => canHost && searchParams.get('new') === '1');
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    scheduledStart: '',
    access: defaultAccess,
    maxParticipants: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [ending, setEnding] = useState(null);
  const [endBusy, setEndBusy] = useState(false);

  // The server lets the class's host (or a platform admin) end it.
  const canEnd = (c) => user?.role === 'admin' || (c.host?.id && c.host.id === user?.id);

  const onEnd = async () => {
    if (!ending) return;
    setEndBusy(true);
    try {
      await conferenceService.end(ending.id);
      toast.success(ending.status === 'live' ? 'Class ended' : 'Class cancelled');
      setEnding(null);
      load();
    } catch (err) {
      // The shared interceptor already toasts everything but 401/403.
      if (err?.status === 401 || err?.status === 403) {
        toast.error(err?.message || 'Only the host can end this class.');
      }
    } finally {
      setEndBusy(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // `silent` so the shared axios interceptor does not also toast: this page
      // renders its own error state, and two notices for one failure is noise.
      const data = await conferenceService.list({}, { silent: true });
      setConferences(data.conferences || []);
      setLiveAvailable(data.liveAvailable === false ? false : true);
    } catch (err) {
      // 503 is the documented "conferencing not provisioned" response, not a
      // fault — it deserves an explanation rather than a generic error.
      setError(
        err?.response?.status === 503
          ? {
              title: 'Live classes are not switched on yet',
              description:
                err?.response?.data?.message ||
                'This server has no LiveKit connection configured, so live sessions cannot run.'
            }
          : {
              title: 'Could not load live classes',
              description: err?.response?.data?.message || 'Something went wrong. Try again.'
            }
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (searchParams.get('new')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const visible = useMemo(() => {
    if (tab === 'all') return conferences;
    if (tab === 'ended') return conferences.filter((c) => c.status === 'ended');
    return conferences.filter((c) => c.status === 'scheduled' || c.status === 'live');
  }, [conferences, tab]);

  const counts = useMemo(
    () => ({
      upcoming: conferences.filter((c) => c.status !== 'ended').length,
      ended: conferences.filter((c) => c.status === 'ended').length,
      all: conferences.length
    }),
    [conferences]
  );

  const onCreate = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.title.trim().length < 3) errs.title = 'Give the class a title of at least 3 characters';
    if (form.maxParticipants) {
      const n = Number(form.maxParticipants);
      if (!Number.isInteger(n) || n < 2 || n > 200) {
        errs.maxParticipants = 'Enter a whole number from 2 to 200';
      }
    }
    if (form.scheduledStart) {
      const start = new Date(form.scheduledStart);
      // A few minutes of slack so "now" typed by hand still counts.
      if (Number.isNaN(start.getTime())) errs.scheduledStart = 'Enter a valid date and time';
      else if (start.getTime() < Date.now() - 5 * 60 * 1000) {
        errs.scheduledStart = 'That time has already passed. Pick a later time or leave it empty.';
      }
    }
    setFormErrors(errs);
    if (Object.keys(errs).length) return;

    setCreating(true);
    try {
      const payload = {
        title: form.title.trim(),
        access: form.access
      };
      // Only send the optional fields when filled: the validators accept them
      // as absent or valid, but not as an empty string.
      if (form.description.trim()) payload.description = form.description.trim();
      if (form.scheduledStart) payload.scheduledStart = new Date(form.scheduledStart).toISOString();
      if (form.maxParticipants) payload.maxParticipants = Number(form.maxParticipants);

      // Posted directly because the shared service's `create` takes no request
      // config; `silent` keeps the error inside the form, next to its field.
      const data = await api.post('/conferences', payload, { silent: true });
      toast.success(liveAvailable === false ? 'Live class scheduled' : 'Live class created');
      setCreateOpen(false);
      setForm({ title: '', description: '', scheduledStart: '', access: defaultAccess, maxParticipants: '' });
      // While live video is off the room can only show "not switched on
      // yet", so opening it straight after creating read as a failure. Stay
      // on the list, where the new class now appears.
      if (data?.conference?.id && liveAvailable !== false) navigate(`/conference/${data.conference.id}`);
      else load();
    } catch (err) {
      const fields = fieldErrorsFrom(err);
      setFormErrors(
        Object.keys(fields).length
          ? fields
          : { title: describeApiError(err) || 'Could not create the class. Try again.' }
      );
    } finally {
      setCreating(false);
    }
  };

  const setField = (name) => (e) => {
    const { value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setFormErrors((prev) => (prev[name] ? { ...prev, [name]: undefined } : prev));
  };

  return (
    <PageShell>
      <PageHeader
        eyebrow="Live"
        title="Live classes"
        lead="Scheduled and in-progress sessions you can join."
        actions={
          canHost && (
            <Button icon={PlusIcon} onClick={() => setCreateOpen(true)}>
              New live class
            </Button>
          )
        }
      />

      {!loading && !error && liveAvailable === false && (
        <div
          role="status"
          className="mb-6 flex items-start gap-3 rounded-2xl border border-saffron-200 bg-saffron-50 px-4 py-3 text-sm text-ink-800"
        >
          <ExclamationTriangleIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-saffron-600" />
          <p>
            <span className="font-semibold">Live video is not switched on yet.</span>{' '}
            {canHost
              ? 'You can schedule classes now, but nobody can join a room until live classes are set up on EduMapping.'
              : 'You can see scheduled classes here, but rooms will not open until live classes are set up. Check back later.'}
          </p>
        </div>
      )}

      {!loading && !error && conferences.length > 0 && (
        <Tabs
          className="mb-6 w-fit"
          value={tab}
          onChange={setTab}
          tabs={TABS.map((t) => ({ ...t, count: counts[t.value] }))}
        />
      )}

      {loading && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      )}

      {!loading && error && (
        <ErrorState title={error.title} description={error.description} onRetry={load} />
      )}

      {!loading && !error && visible.length === 0 && (
        <EmptyState
          icon={VideoCameraIcon}
          title={conferences.length === 0 ? 'No live classes yet' : 'Nothing in this view'}
          description={
            conferences.length === 0
              ? canHost
                ? 'Create one to start teaching. Students in your organization will see it here.'
                : 'When your institution schedules a live class, it will appear here.'
              : 'Try another tab.'
          }
          action={
            canHost && conferences.length === 0 ? (
              <Button icon={PlusIcon} onClick={() => setCreateOpen(true)}>
                New live class
              </Button>
            ) : (
              <Button as={Link} to="/dashboard" variant="secondary">
                Back to dashboard
              </Button>
            )
          }
        />
      )}

      {!loading && !error && visible.length > 0 && (
        <div className={cx('grid gap-4 sm:grid-cols-2 lg:grid-cols-3')}>
          {visible.map((c) => (
            <ConferenceCard
              key={c.id}
              conference={c}
              onJoin={(conf) => navigate(`/conference/${conf.id}`)}
              canEnd={canEnd(c)}
              onEnd={setEnding}
            />
          ))}
        </div>
      )}

      <AdminModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="New live class"
        description={
          hasOrg
            ? 'Students in your institution will be able to join.'
            : 'Choose who can join below.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" form="create-conference" loading={creating}>
              {liveAvailable === false ? 'Schedule class' : 'Create and open'}
            </Button>
          </>
        }
      >
        <form id="create-conference" onSubmit={onCreate} className="space-y-4">
          <Input
            label="Title"
            required
            value={form.title}
            onChange={setField('title')}
            error={formErrors.title}
            placeholder="Aptitude prep — session 3"
          />
          <Textarea
            label="Description"
            rows={3}
            value={form.description}
            onChange={setField('description')}
            help="Optional. What you'll cover."
          />
          <Input
            label="Scheduled start"
            type="datetime-local"
            value={form.scheduledStart}
            onChange={setField('scheduledStart')}
            error={formErrors.scheduledStart}
            help="Leave empty to start immediately."
          />
          <Select
            label="Who can join"
            value={form.access}
            onChange={setField('access')}
            error={formErrors.access}
            // "Registered for an event" and "invite only" are left out: this
            // form cannot link an event or invite anyone, so either choice
            // created a room that nobody could enter.
            options={[
              ...(hasOrg ? [{ value: 'organization', label: 'Anyone in my institution' }] : []),
              { value: 'public', label: 'Anyone signed in to EduMapping' }
            ]}
          />
          <Input
            label="Participant limit"
            type="number"
            min={2}
            max={200}
            value={form.maxParticipants}
            onChange={setField('maxParticipants')}
            error={formErrors.maxParticipants}
            help="Optional. This server is sized for roughly 25–30 people at once."
          />
        </form>
      </AdminModal>

      <AdminModal
        open={Boolean(ending)}
        onClose={() => !endBusy && setEnding(null)}
        size="md"
        title={ending?.status === 'live' ? 'End this class for everyone?' : 'Cancel this class?'}
        description={
          ending?.status === 'live'
            ? 'Everyone in the room is disconnected and nobody can rejoin.'
            : 'It moves to Past and nobody can join it. This cannot be undone.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setEnding(null)} disabled={endBusy}>
              Keep class
            </Button>
            <Button variant="danger" loading={endBusy} onClick={onEnd}>
              {ending?.status === 'live' ? 'End class' : 'Cancel class'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-700">{ending?.title}</p>
      </AdminModal>
    </PageShell>
  );
};

export default ConferencesList;
